import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { isNil } from "lodash";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { GenreMergeButton } from "@/pages/genres/components/genre-merge-button";
import { GenrePreview } from "@/pages/genres/components/genre-preview";
import { useGenreRepository } from "@/pages/genres/hooks/use-genre-repository";
import {
  GenresEditFormType,
  GenresReadType,
  genresEditSchema,
  genresReadSchema,
} from "@/pages/genres/schemas/genres.edit.form";

const schemaProvider = new ZodProvider(genresEditSchema);

export const GenresEdit = () => {
  const { get, getUsageCount, update } = useGenreRepository();
  const { id } = useParsed();
  // Held in the LENIENT read shape so the preview can show the
  // enrichment lifecycle fields the edit form must not be able to submit.
  const [values, setValues] = React.useState<GenresReadType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();

  const setTitle = useDocumentTitle();
  const { list } = useNavigation();

  const [usedOn, setUsedOn] = React.useState<number>(0);
  const [usedOnArtists, setUsedOnArtists] = React.useState<number>(0);

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id!.toString())
      .then((response) => {
        // Lenient read + raw fallback: a hard `.parse()` here blanked the
        // whole genre edit page on one unexpected field.
        const data = parseRead(genresReadSchema, response, "genre");
        setValues(data);
        setTitle("Edit " + data.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get genre",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => {
    if (!isNil(id)) {
      getUsageCount(id.toString()).then((result) => {
        setUsedOn(result.events);
        setUsedOnArtists(result.artists);
      });
    }
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<GenresEditFormType, any, GenresEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: GenresEditFormType,
    form: UseFormReturn<GenresEditFormType, any, GenresEditFormType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await update({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values as Partial<GenresEditFormType>}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents()}
          >
            <BottomActionBar className="justify-between">
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              {usedOn > 0 || usedOnArtists > 0 ? (
                <GenreMergeButton id={id!.toString()} />
              ) : (
                <DeleteButton
                  resource="genres"
                  onSuccess={() => {
                    list("genres");
                  }}
                />
              )}
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
      {values && (
        <GenrePreview
          genre={values}
          usedOn={usedOn}
          usedOnArtists={usedOnArtists}
        />
      )}
    </div>
  );
};
